'use client';
import { Flex, font, themedColor } from '@lemonade-hq/cdk';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import styled from 'styled-components';
import { ReviewProduct, ReviewType, TextReview, VideoReview } from './types';
import { TextReviewCard } from './ReviewCard/TextReviewCard';
import { VideoReviewCard } from './ReviewCard/VideoReviewCard';
import { Filter } from './Filter';
import { PlayerDialog } from './Player/PlayerDialog';
import { carReviews, homeReviews, lifeReviews, petReviews, rentersReviews, allProductReviews } from './reviews';

const ReviewsWrapper = styled.div`
  display: grid;
  ${MEDIA_QUERIES.desktop`
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    max-width: 1036px;
    margin-bottom: 48px;
  `}
  ${MEDIA_QUERIES.tablet`
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    max-width: 766px;
    margin-inline: 5px;
    margin-bottom: 48px;
  `}
  ${MEDIA_QUERIES.mobile`
    width: 100%;
    grid-template-columns: repeat(3, 1fr);
    gap: 2px;
    padding-inline: 5px;
    margin-bottom: 24px;
  `}
`;

const Disclosure = styled.span`
  ${font('main', { fontSize: '1.8rem', fontWeight: 400, lineHeight: 1.45 })};
  ${MEDIA_QUERIES.mobile`
      padding-inline: 1.5rem;
      font-size: 1.4rem;
      white-space: pre-line;
  `}
  color: ${themedColor('invertedTextSecondary')};
  text-align: center;
  margin-top: 24px;
`;

function getReviewsByProduct(product: ReviewProduct): (VideoReview | TextReview)[] {
  const reviewsByProduct: Record<ReviewProduct, (VideoReview | TextReview)[]> = {
    [ReviewProduct.Car]: carReviews,
    [ReviewProduct.Home]: homeReviews,
    [ReviewProduct.Life]: lifeReviews,
    [ReviewProduct.Pet]: petReviews,
    [ReviewProduct.Renters]: rentersReviews,
    [ReviewProduct.All]: allProductReviews,
  };

  return (reviewsByProduct[product] || reviewsByProduct[ReviewProduct.All]).map((review, index) => ({
    ...review,
    id: index + 1,
  }));
}

interface ReviewsProps {
  initialProduct?: ReviewProduct;
  videosDisclosure?: string;
}

export const ReviewsSection: React.FC<ReviewsProps> = ({ initialProduct = ReviewProduct.All, videosDisclosure }) => {
  const [selectedFilter, setSelectedFilter] = useState<ReviewProduct>(initialProduct);
  const [selectedReview, setSelectedReview] = useState<number | null>(null);
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);

  const filteredReviews = useMemo(() => getReviewsByProduct(selectedFilter), [selectedFilter]);

  useEffect(() => {
    const handleScroll = () => {
      if (!hasScrolled && window.scrollY > 20) {
        setHasScrolled(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasScrolled]);

  const onReviewClick = useCallback(
    (reviewId?: number) => {
      if (!reviewId) return;

      setSelectedReview(reviewId);
    },
    [setSelectedReview],
  );

  return (
    <Flex alignItems="center" flexDirection="column" mb={['24px', '48px', '48px', '48px']}>
      <ReviewsWrapper>
        <AnimatePresence>
          {filteredReviews.map(review => (
            <motion.div
              key={`${review.id}-${selectedFilter}`}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              whileHover={{
                scale: 1.05,
              }}
              transition={{ duration: 0.3 }}
            >
              {review.type === ReviewType.Text ? (
                <TextReviewCard review={review} onClick={() => onReviewClick(review.id)} />
              ) : (
                <VideoReviewCard review={review} onClick={() => onReviewClick(review.id)} />
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </ReviewsWrapper>

      <AnimatePresence>
        {hasScrolled && <Filter selectedFilter={selectedFilter} setSelectedFilter={setSelectedFilter} />}
      </AnimatePresence>
      <Disclosure>{videosDisclosure}</Disclosure>
      {selectedReview !== null && (
        <PlayerDialog
          reviews={filteredReviews}
          initialReview={selectedReview}
          onClose={() => setSelectedReview(null)}
          videosDisclosure={videosDisclosure}
        />
      )}
    </Flex>
  );
};
